// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The HUD's "read machine aloud" (G2-09; 06 §6, NFR-022, NFR-025).
//
// > *"A 'read machine aloud' control speaks the current ball, score, active mode, timer, what is lit
// > and what the next objective is — the direct analogue of Chess VR's board narration, free on
// > every tier."*
//
// **On the machine and not in Settings**, for `SoundControl`'s reason one step further: the moment
// somebody wants this is the moment they have lost track of what the machine is doing, and a
// control they have to leave the game to reach is one they cannot use while the ball is live.
//
// **It writes as well as speaks** (NFR-022). The same sentences go into a live region, so the
// report reaches a player who cannot hear it, a player whose sound is muted, and a player whose
// browser has no speech synthesis at all — which is a real state and not an edge case: `Chrome` on
// a fresh Linux install has no voices, and `speechSynthesis.speak` there is a call that returns
// and does nothing.
//
// **Speech is cancelled before it starts.** Pressing this twice in a row means "say it again, I
// missed it", not "queue a second copy behind the first" — and a synthesizer that queues will
// happily read a report from thirty seconds ago over a machine that has moved on.
//
// Geometry rather than an emoji, like every other glyph in this HUD: an emoji is a picture from the
// operating system's font, so it is a different picture on Windows, Android and the Quest browser,
// and a box wherever the font is monochrome.

import { useCallback, useState } from 'react'
import { useLocalisation } from '../i18n/useLanguage'
import { aloudSentences, readMachineAloud, type AloudSnapshot } from '../machineAloud'

export interface ReadAloudProps {
  /**
   * The machine as it is right now. A function, because the snapshot walks every lamp on the
   * playfield and this is asked for a few times a minute rather than every frame.
   *
   * `undefined` before the machine is built, which is the first frame or two of the page.
   */
  readonly report: () => AloudSnapshot | undefined
}

/** What a browser has to provide for this to be spoken rather than only written. */
export interface SpeechLike {
  cancel: () => void
  speak: (utterance: SpeechSynthesisUtterance) => void
}

/**
 * Say it, and answer whether anything was actually said.
 *
 * Exported and taking its synthesizer as an argument so a test can drive both branches without a
 * browser — the interesting one being the browser that has no speech at all, where the report still
 * has to reach the live region.
 */
export function speakReport(
  sentences: readonly string[],
  speech: SpeechLike | undefined = globalThis.speechSynthesis,
  make: (text: string) => SpeechSynthesisUtterance = (text) => new SpeechSynthesisUtterance(text),
): boolean {
  if (speech === undefined || sentences.length === 0) return false
  try {
    // **Cancelled first.** A second press means "say it again", and a queue means the player hears
    // a report about a machine that has since moved on.
    speech.cancel()
    // One utterance per sentence, so a synthesizer pauses between them — handed the whole
    // paragraph, several read it as one breath.
    for (const line of sentences) speech.speak(make(line))
    return true
  } catch {
    // A browser that refused is a browser this control writes to rather than speaks through. There
    // is nothing to report and nothing to recover: the text is already on screen.
    return false
  }
}

export function ReadAloud({ report }: ReadAloudProps): React.JSX.Element {
  const { t } = useLocalisation()
  const [said, setSaid] = useState('')

  const speak = useCallback(() => {
    const snapshot = report()
    if (snapshot === undefined) return
    // **Two shapes of the same report, and each is the right one for its half.** The synthesizer
    // gets a sentence per utterance so it pauses between them — handed one paragraph, several read
    // it in a single breath — and the caption gets the paragraph, because a list of `<p>`s for four
    // short sentences is four things for a screen reader to announce separately.
    speakReport(aloudSentences(snapshot))
    setSaid(readMachineAloud(snapshot))
  }, [report])

  return (
    <>
      <button
        type="button"
        className="button button--quiet stage__aloud"
        title={t('stage.aloud-title')}
        onClick={speak}
      >
        <AloudGlyph />
        <span className="sr-only">{t('stage.aloud')}</span>
      </button>
      {/*
        The written half of NFR-022, and the reason this component owns a piece of state at all.

        `role="status"` rather than `alert`: the player asked for this, so it is an answer rather
        than an interruption, and a polite region is read after whatever the machine is already
        saying instead of cutting across it.

        Visible as well as read out. A caption that only a screen reader could reach would miss the
        player this is most obviously for — somebody with the sound off in a quiet room — and 06 §6
        asks for the text on screen in as many words.
      */}
      {said === '' ? null : (
        <p className="stage__aloudText" role="status" aria-live="polite">
          {said}
        </p>
      )}
    </>
  )
}

/**
 * A speaker with sound lines coming out of it, over a dotted panel.
 *
 * Deliberately not the cabinet grille `SoundControl` draws or the cone `VoiceControls` draws: all
 * three can be on screen at once, and three speakers side by side would be three buttons a player
 * has to press to tell apart. This one is a *display being read*, so the panel is the subject and
 * the speaker is the small part.
 */
function AloudGlyph(): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      {/* The panel — a DMD, which is the thing being read out. */}
      <rect x="2" y="4" width="14" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5 8h8M5 11h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      {/* Two arcs leaving it. Round caps, like every other glyph in this HUD. */}
      <path
        d="M17.5 9.5a3.2 3.2 0 0 1 0 4.6M20 7.5a6.4 6.4 0 0 1 0 8.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}
